// app/javascript/compcalc/components/Commutation.jsx

import React from "react";
import PropTypes from "prop-types";
import { withStyles } from '@material-ui/core/styles';

import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';

import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormLabel from '@material-ui/core/FormLabel';
import ListItem from '@material-ui/core/ListItem';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import MenuIcon from '@material-ui/icons/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';

import moment from "moment";

import DateFnsUtils from '@date-io/date-fns';
import { MuiPickersUtilsProvider, KeyboardDatePicker as DatePicker } from '@material-ui/pickers';

import withErrorHandling from './ErrorHandling';
import { currencyFormatter, isValidDate, formatShortDate, formatLongDate, saveAsFile, sendMail } from '../utils';
import { presentValue } from '../pv_utils';


const styles = theme => ({
  root: {
    flexGrow: 1,
    marginTop: theme.spacing(1),
  },
  group: {
    display: 'flex',
    flexFlow: 'row nowrap',
  },
  alignCenter: {
    // background: 'green',
    display: 'flex',
    justifyContent: 'center',    // Horizontal align.
    alignItems: 'center',        // Vertical align.
  },
  alignLeft: {
    // background: 'blue',
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',        // Vertical align.
  },
  alignRight: {
    // background: 'red',
    display: 'flex',
    flexFlow: 'row-reverse nowrap',
    alignItems: 'center',
  },
  paper: {
    padding: theme.spacing(2),
    display: 'inline-block',
  },
  wrapContent: {
    padding: theme.spacing(2),
    display: 'inline-block',
  },
  pre: {
    width: '100%',
    maxWidth: '800px',
    whiteSpace: 'pre-wrap',
  }
});


class Commutation extends React.PureComponent {

  constructor() {
    super();

    this.itemsDiscount = [];
    for (let i = 0; i <= 15*4; ++i) {
      let f = parseFloat(i) / 4.0;
      this.itemsDiscount.push(
        <MenuItem dense key={ 'discount' + i } value={ i }>{ f }%</MenuItem>
      );
    }

    this.compounds = [ 'Annually', 'Semi-annually', 'Quaterly', 'Monthly' ];
    this.itemsCompound = [];
    for (let i = 0; i < this.compounds.length; ++i) {
      this.itemsCompound.push(
        <MenuItem dense key={ 'compound' + i } value={ i }>{ this.compounds[i] }</MenuItem>
      );
    }

    this.itemsPercentages = [];
    for (let i = 0; i < 100; ++i) {
      this.itemsPercentages.push(
        <MenuItem dense key={'percent' + i} value={i}>{i}%</MenuItem>
      );
    }

    this.minDOI = new Date('1996-01-01T12:00');
    this.maxDOI = new Date('2024-12-31T12:00');

    let initDOI = new Date('2015-01-01T12:00');
    let initDOC = new Date('2016-01-01T12:00');

    this.pdRateMaxText = 'PD Weekly Rate (max)';
    this.pdRateMinText = 'PD Weekly Rate (min)';
    this.pdRateText = 'PD Weekly Rate';

    this.initialState = {
      pd: 50, // 50%
      doi: initDOI,
      doc: initDOC,
      increase: 1,
      increaseEnabled: false,

      fee: currencyFormatter.format(0),
      pda: currencyFormatter.format(0),
      discount: 12,

      inputAww: false,
      aww: currencyFormatter.format(0),
      pdRateText: this.pdRateMaxText,
      pdRate: currencyFormatter.format(0),

      inputAmount: false,
      amount: currencyFormatter.format(0),
      weeks: 0,

      commuted: 0,
      left2pay: 0,
      weeks2pay: 0,

      isExpanded: false,
    };

    this.state = this.initialState;
  }

  componentDidMount = () => {
    this.props.setChildMenuHandler(this.handleMenu);
    this.fetchPD();
  }

  fetchPD = () => {
    let row = this.state.pd;
    let date = this.state.doi;
    let aww = this.state.inputAww ? currencyFormatter.parse(this.state.aww) : 0.0;

    let url = `api/v1/permanent_disability.json?disability=${row}&date=${formatShortDate(date)}&aww=${aww}`;

    this.props.fetch(url, results => {
      // console.log(results);
      let newState = {
        minAWW: results.minAWW,
        maxAWW: results.maxAWW,
        aww: currencyFormatter.format(results.aww),
        minPDRate: results.minPDRate,
        maxPDRate: results.maxPDRate,
        pdRate: currencyFormatter.format(results.pdRate),
        weeks: results.weeks,
        inputAmount: false,
        amount: currencyFormatter.format(results.amount),
      };
      this.setState(newState, this.calculate);
    }).catch(e => this.props.showError(e, 'Error obtaining permanent disability amount from server'));
  }

  // ------------------------ Events ------------------------
  onTextExpand = () => {
    this.setState({ isExpanded: !this.state.isExpanded });
  }

  onDateOfInjuryChanged = (date) => {
    if (!isValidDate(date)) { return; }

    let doc = this.state.doc;
    if (doc < date)
      doc = date;

    this.setState({ doi: date, doc: doc }, this.fetchPD);
  }

  onDateOfCommutationChanged = (date) => {
    if (!isValidDate(date)) { return; }

    this.setState({ doc: date }, this.calculateInterest);
  }

  onSelectChange = (event) => {
    // console.log(event.target.name, event.target.value);
    this.setState({ [event.target.name]: event.target.value }, this.fetchPD);
  };

  onInputAwwChanged = (checked) => {
    if (this.state.inputAww == checked) { return; }
    this.setState({
      inputAww: checked
    }, this.fetchPD);
  }

  onAwwChanged = (value) => {
    if (!this.state.inputAww) return;
    let aww = currencyFormatter.parse(value);
    if (isNaN(aww))
      aww = 0;

    let pdRate = aww * 2.0/3.0;
    // console.log(`onAwwChanged(): ${aww}`);
    this.setState({
      aww: currencyFormatter.format(aww),
      pdRate: currencyFormatter.format(pdRate),
    }, this.fetchPD);
  }

  onPdRateChanged = (value) => {
    if (!this.state.inputAww) return;
    let pdRate = currencyFormatter.parse(value);
    if (isNaN(pdRate))
      pdRate = 0;

    // console.log(`onPdRateChanged(): ${pdRate}`);

    let aww = pdRate * 3.0/2.0;
    this.setState({
      aww: currencyFormatter.format(aww),
      pdRate: currencyFormatter.format(pdRate),
    }, this.fetchPD);
  }

  onDiscountIndexChanged = (value) => {
    this.setState({ discount: value }, this.calculateInterest);
  }

  onFeeChanged = (value) => {
    value = currencyFormatter.parse(value);
    if (isNaN(value))
      value = 0;
    this.setState({ fee: currencyFormatter.format(value) }, this.calculateInterest);
  }

  onPDAChanged = (value) => {
    value = currencyFormatter.parse(value);
    if (isNaN(value))
      value = 0;
    this.setState({ pda: currencyFormatter.format(value) }, this.calculateInterest);
  }

  onInputAmountChanged = (checked) => {
    if (this.state.inputAmount == checked) { return; }
    this.setState({ inputAmount: checked }, this.calculateInterest);
  }

  onAmountChanged = (value) => {
    if (!this.state.inputAmount) return;
    value = currencyFormatter.parse(value);
    if (isNaN(value))
      value = 0;
    this.setState({ amount: currencyFormatter.format(value) }, this.calculateInterest);
  }

  // --------------------- Calculations ---------------------
  calculate = () => {
    let weeks = this.state.weeks, amount = currencyFormatter.parse(this.state.amount);

    let year = this.state.doi.getFullYear();
    let increaseEnabled = 2006 <= year && year <= 2012;

    let increaseIndex = increaseEnabled ? this.state.increase : 1;

    let increase = (increaseIndex - 1) * 0.15; // -15%, 0, 15%.
    if (increase > 0.0) {
      let days = weeks * 7.0; // to days
      if (days > 60.0) {
        amount /= days; // amount per day.
        let newAmount = amount * 60.0;
        days -= 60.0; // first 60 days not paying increase
        increase *= amount;
        amount += increase; // amount per day increased
        newAmount += days*amount; // add other days with increase
        amount = newAmount;
      }
    }
    else {
      increase = amount * increase;
      amount += increase;
    }

    let aww = currencyFormatter.parse(this.state.aww);
    let pdRate = currencyFormatter.parse(this.state.pdRate);
    let pdRateText = pdRate <= this.state.minPDRate ? this.pdRateMinText :
                     pdRate >= this.state.maxPDRate ? this.pdRateMaxText : this.pdRateText;

    let newState = {
      pdRateText: pdRateText,
      amount: currencyFormatter.format(amount),
      increase: increaseIndex,
      increaseEnabled: increaseEnabled,
    };
    if (!this.state.inputAww || aww <= 0.0)
      newState.aww = currencyFormatter.format(this.state.maxAWW);
    this.setState(newState, this.calculateInterest);
  }

  calculateInterest = () => {
    let weeks = this.state.weeks, amount = currencyFormatter.parse(this.state.amount);
    let pdRate = currencyFormatter.parse(this.state.pdRate);

    let fee = currencyFormatter.parse(this.state.fee);
    let pda = currencyFormatter.parse(this.state.pda); // PD advances.

    let pdaWeeks = pda / pdRate;
    let weeksLeft = this.state.weeks - pdaWeeks;
    console.log("weeks left: ", weeksLeft);

    let interestTime = (amount - fee - pda) / pdRate * 7; // In days.
    // let remainingTime = interestTime - Math.floor(interestTime);
    // let remainingAmount = remainingTime * pdRate / 7.0;
    console.log("interest time: ", interestTime);

    let today = new Date();
    let start = moment(today <= this.state.doc ? today : this.state.doc);
    let end = moment(this.state.doc);
    end.add(interestTime, 'days');

    let years = end.diff(start, 'years');
    start.add(years, 'years');

    let days = end.diff(start, 'days');
    years += days / 365.0;

    console.log("Years: ", years);

    let yearlyRate = pdRate * 365.0 / 7.0;
    let commuted = presentValue(this.state.discount * 0.25 / 100.0, fee, years);

    let left2pay = amount - pda - commuted;
    let weeks2pay = left2pay / pdRate;

    this.setState({
      commuted: commuted,
      left2pay: left2pay,
      weeks2pay: Math.ceil(weeks2pay * 100.0) / 100.0,
    });
  }

  // --------------------- Menu functions ---------------------
  handleMenu = (menu, submenu) => {
    // console.log(`handleMenu: menu = ${menu}, submenu = ${submenu}`);
    switch (submenu) {
      case 'save':
        this.handleMenuSave();
        break;
      case 'load':
        this.handleMenuLoad();
        break;
      case 'mail':
        this.handleMenuMail();
        break;
      case 'copy':
        this.handleMenuCopy();
        break;
      case 'clear':
        this.handleMenuClear();
    }
  }

  handleMenuSave = () => {
    let json = {
      pd: this.state.pd,
      doi: this.state.doi,
      doc: this.state.doc,
      increase: this.state.increase,
      increaseEnabled: this.state.increaseEnabled,

      fee: this.state.fee,
      pda: this.state.pda,
      discount: this.state.discount,

      inputAww: this.state.inputAww,
      aww: this.state.aww,
      pdRateText: this.state.pdRateText,
      pdRate: this.state.pdRate,

      inputAmount: this.state.inputAmount,
      amount: this.state.amount,
      weeks: this.state.weeks,
    };
    let body = JSON.stringify(json, null, 4);
    saveAsFile('commutation.json', body);
  }

  handleMenuLoad = () => {
    this.refs.fileUploader.click();
  }

  onUploadFile = (e) => {
    e.stopPropagation();
    e.preventDefault();
    let file = e.target.files[0];

    let reader = new FileReader();
    reader.addEventListener('load', (e) => {
      // console.log(e.target.result);
      try {
        let hash = JSON.parse(e.target.result);
        hash.doi = new Date(hash.doi);
        hash.doc = new Date(hash.doc);

        this.setState(hash, this.calculate);
      }
      catch (e) {
        console.error(e);
      }
    });
    reader.readAsBinaryString(file);
  }

  getCopyString = () => {
    let increase = this.state.increaseEnabled && this.state.increase != 1 ? (this.state.increase == 0 ? 'INCREASE ENABLED: -15%\n' : 'INCREASE ENABLED: 15%\n') : '';

    return `PERMANENT DISABILITY: ${this.state.pd}%
DATE OF INJURY: ${formatLongDate(this.state.doi)}
DATE OF COMMUTATION / AWARD: ${formatLongDate(this.state.doc)}
${increase}
ATTORNEY'S FEES: ${this.state.fee}
PD ADVANCES: ${this.state.pda}

COMMUTATION INTEREST RATE: ${parseFloat(this.state.discount) * 0.25}%

AVERAGE WEEKLY WAGE: ${this.state.aww}
PD WEEKLY RATE: ${this.state.pdRate}

TOTAL PD AWARDED: ${this.state.amount}
WEEKS OF PAYMENT: ${this.state.weeks}

CREDIT FOR ATTORNEY'S FEES AFTER COMMUTATION: ${currencyFormatter.format(this.state.commuted)}
REMAIN PD OWED AFTER DEDUCTION OF PDA AND CREDIT FOR ATTONREY'S FEES AFTER COMMUTATION: ${currencyFormatter.format(this.state.left2pay)}
WEEKS OF REMAINING PAYMENTS: ${this.state.weeks2pay}

--------------------------------------------------------------
`;
//CompCalc Plus: “the Complex made Simple ...”

  }

  handleMenuMail = () => {
    let s = this.getCopyString();
    sendMail('', 'Commutation of Attorney\'s Fees', s);
  }

  handleMenuCopy = () => {
    this.props.copyToClipboard(this.getCopyString());
  }

  handleMenuClear = () => {
    this.setState(this.initialState, this.calculate);
  }

  // ------------------------ Render ------------------------
  render() {
    const { classes } = this.props;

    return (
      <MuiPickersUtilsProvider utils={ DateFnsUtils }>
      <input type="file" id="file" ref="fileUploader" style={{display: "none"}} onChange={this.onUploadFile}/>
      <Grid container spacing={1} className={classes.root} justifyContent="center">
        <Grid item xs={4} className={classes.alignCenter}>
          {/* Permanent Disability */}
          <Paper className={classes.paper}>
            <FormControl>
              {/*<Typography variant="h6">Permanent Disability</Typography>*/}
              {/*<InputLabel htmlFor="pd-auto-width">Permanent Disability</InputLabel>*/}
              <FormLabel component="legend">Permanent Disability</FormLabel>
              {/*For select: MenuProps={ {anchorOrigin: { horizontal: 'right', vertical: 'top' } } }*/}
              <Select
                value={ this.state.pd }
                onChange={this.onSelectChange}
                input={<Input id="pd" name="pd" />}
                autoWidth
              >
                { this.itemsPercentages }
              </Select>
              {/*<FormHelperText>Auto width</FormHelperText>*/}
            </FormControl>
          </Paper>
        </Grid>

        {/* Date of Injury */}
        <Grid item xs={4} className={classes.alignCenter}>
          <Paper className={classes.wrapContent}>
            <div className="picker">
              <DatePicker
                variant="outlined"
                label="Date of Injury"
                minDate={ this.minDOI }
                maxDate={ this.maxDOI }
                value={ this.state.doi }
                onKeyPress={ e => e.key == 'Enter' && e.target.blur() }
                onChange={ this.onDateOfInjuryChanged }
                format={ 'MM/dd/yyyy' }
              />
            </div>
          </Paper>
        </Grid>

        {/* Date of Commutation / Award */}
        <Grid item xs={4} className={classes.alignCenter}>
          <Paper className={classes.wrapContent}>
            <div className="picker">
              <DatePicker
                variant="outlined"
                label="Date of Commutation / Award"
                minDate={ this.state.doi }
                value={ this.state.doc }
                onChange={ this.onDateOfCommutationChanged }
                format={ 'MM/dd/yyyy' }
              />
            </div>
          </Paper>
        </Grid>

        {/* 15% Increase */}
        <Grid item xs={3} className={classes.alignCenter}>
          <Paper className={classes.paper}>
            <FormControl component="fieldset">
              <FormLabel component="legend">15% Increase / Decrease</FormLabel>
              <RadioGroup
                aria-label="Increase"
                name="increase"
                className={ classes.group }
                value={ this.state.increase.toString() }
                onChange={this.onSelectChange}
              >
                <FormControlLabel value="0" disabled={!this.state.increaseEnabled} control={<Radio color="primary" />} label="-15%" />
                <FormControlLabel value="1" disabled={!this.state.increaseEnabled} control={<Radio color="primary" />} label="none" />
                <FormControlLabel value="2" disabled={!this.state.increaseEnabled} control={<Radio color="primary" />} label="+15%" />
              </RadioGroup>
            </FormControl>
          </Paper>
        </Grid>

        {/* Attorney's Fee */}
        <Grid item xs={3} className={classes.alignCenter}>
          <Paper className={classes.paper}>
            <FormLabel component="legend">Attorney&#39;s Fees</FormLabel>
            <TextField
              id="pda"
              value={ this.state.fee }
              onKeyPress={ e => e.key == 'Enter' && e.target.blur() }
              onChange={ (e) => this.setState({ fee: e.target.value }) }
              onFocus={ (e) => e.target.select() }
              onBlur={ (e) => this.onFeeChanged(e.target.value) }
              className={ classes.textField }
              margin="normal"
            />
          </Paper>
        </Grid>

        {/* PD Advances */}
        <Grid item xs={3} className={classes.alignCenter}>
          <Paper className={classes.paper}>
            <FormLabel component="legend">PD Advances</FormLabel>
            <TextField
              id="pda"
              value={ this.state.pda }
              onKeyPress={ e => e.key == 'Enter' && e.target.blur() }
              onChange={ (e) => this.setState({ pda: e.target.value }) }
              onFocus={ (e) => e.target.select() }
              onBlur={ (e) => this.onPDAChanged(e.target.value) }
              className={ classes.textField }
              margin="normal"
            />
          </Paper>
        </Grid>

        {/* PV Discount Rate */}
        <Grid item xs={3} className={classes.alignCenter}>
          <Paper className={classes.wrapContent}>
            <FormControl>
              <FormLabel component="legend">Commutation Interest Rate</FormLabel>
              <Select
                value={ this.state.discount }
                onChange={ e => this.onDiscountIndexChanged(e.target.value) }
                input={<Input id="discount" name="discount" />}
                autoWidth
              >
                { this.itemsDiscount }
              </Select>
            </FormControl>
          </Paper>
        </Grid>

        {/* Input AWW */}
        <Grid item xs={3}  className={classes.alignCenter}>
          <Paper className={classes.wrapContent}>
            <FormControl>
              <FormControlLabel style={{ marginRight: -14 }} control={
                <ListItem button dense key='inputAww'>
                  <ListItemSecondaryAction>
                    <Checkbox
                      checked={ this.state.inputAww }
                      onChange={ (e) => this.onInputAwwChanged(e.target.checked) }
                      color="primary"
                    />
                  </ListItemSecondaryAction>
                  <ListItemText primary='Input AWW' />
                </ListItem>
              } />
              <TextField
                id="aww"
                value={ this.state.aww }
                onKeyPress={ e => e.key == 'Enter' && e.target.blur() }
                onChange={ (e) => this.setState({ aww: e.target.value, inputAww: true }) }
                onFocus={ (e) => e.target.select() }
                onBlur={ (e) => this.onAwwChanged(e.target.value) }
                className={ classes.textField }
                margin="dense"
                InputProps={{ readOnly: false }}
              />
            </FormControl>
          </Paper>
        </Grid>

        {/* PD Rate */}
        <Grid item xs={3}  className={classes.alignCenter}>
          <Paper className={classes.wrapContent}>
            <FormControl>
              <FormControlLabel style={{ marginRight: -14 }} control={
                <ListItem button dense key='inputPdRate'>
                  <ListItemSecondaryAction>
                    <Checkbox
                      checked={ this.state.inputAww }
                      onChange={ (e) => this.onInputAwwChanged(e.target.checked) }
                      color="primary"
                    />
                  </ListItemSecondaryAction>
                  <ListItemText primary={ this.state.pdRateText } />
                </ListItem>
              } />
              <TextField
                id="pdRate"
                value={ this.state.pdRate }
                onKeyPress={ e => e.key == 'Enter' && e.target.blur() }
                onChange={ (e) => this.setState({ pdRate: e.target.value, inputAww: true }) }
                onFocus={ (e) => e.target.select() }
                onBlur={ (e) => this.onPdRateChanged(e.target.value) }
                className={ classes.textField }
                margin="dense"
                InputProps={{ readOnly: false }}
              />
            </FormControl>
          </Paper>
        </Grid>

        {/* Total Award */}
        <Grid item xs={3} className={classes.alignCenter}>
          <Paper className={classes.paper}>
            <FormControl>
              <FormControlLabel style={{ marginRight: -14 }} control={
                <ListItem button dense key='inputAward'>
                  <ListItemSecondaryAction>
                    <Checkbox
                      checked={ this.state.inputAmount }
                      onChange={ (e) => this.onInputAmountChanged(e.target.checked) }
                      color="primary"
                    />
                  </ListItemSecondaryAction>
                  <ListItemText primary='Total PD Awarded' />
                </ListItem>
              } />
              <TextField
                id="award"
                value={ this.state.amount }
                onKeyPress={ e => e.key == 'Enter' && e.target.blur() }
                onChange={ (e) => this.setState({ amount: e.target.value, inputAmount: true }) }
                onFocus={ (e) => e.target.select() }
                onBlur={ (e) => this.onAmountChanged(e.target.value) }
                className={ classes.textField }
                margin="dense"
                InputProps={{ readOnly: false }}
              />
            </FormControl>
          </Paper>
        </Grid>

        {/* Weeks of PD Payment */}
        <Grid item xs={3}  className={classes.alignCenter}>
          <Paper className={classes.paper}>
          <FormControl>
            <FormLabel component="legend">Weeks of PD Payment</FormLabel>
            <TextField
              id="weeks"
              value={ this.state.weeks.toString() }
              className={ classes.textField }
              margin="normal"
              InputProps={{ readOnly: true }}
            />
          </FormControl>

          </Paper>
        </Grid>

        {/* Commuted Attorney's Fee */}
        <Grid item xs={4} className={classes.alignCenter}>
          <Paper className={classes.paper}>
            <FormLabel component="legend">Credit for Attorney&#39;s Fees After Commutation</FormLabel>
            <TextField
              id="commuted"
              value={ currencyFormatter.format(this.state.commuted) }
              className={ classes.textField }
              margin="normal"
              InputProps={{ readOnly: true }}
            />
          </Paper>
        </Grid>

        {/* Remaining payments of PD */}
        <Grid item xs={4} className={classes.alignCenter}>
          <Paper className={classes.paper}>
            <FormLabel component="legend">
              Remain PD Owed after Deduction of PDA and Credit for Attonrey&#39;s Fees after Commutation
            </FormLabel>
            <TextField
              id="left2pay"
              value={ currencyFormatter.format(this.state.left2pay) }
              className={ classes.textField }
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />
          </Paper>
        </Grid>

        {/* Time of Remaining Payments */}
        <Grid item xs={4} className={classes.alignCenter}>
          <Paper className={classes.paper}>
            <FormLabel component="legend">Weeks of Remaining Payments</FormLabel>
            <TextField
              id="weeks2pay"
              value={ this.state.weeks2pay }
              className={ classes.textField }
              margin="normal"
              InputProps={{ readOnly: true }}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} className={classes.alignCenter}>
          <Paper className={classes.paper}>
            <div className={classes.pre}>
              <Typography variant="h6">
                {text1}
              </Typography>
              <Typography variant="body1">
                {text2}
              </Typography>
              {this.state.isExpanded && (
                <div>
                  <br/>
                  <Typography variant="h6">
                    {text3}
                  </Typography>
                  <Typography variant="body1">
                    {text4}
                  </Typography>
                  <br/>
                  <Typography variant="h6">
                    {text5}
                  </Typography>
                  <Typography variant="body1">
                    {text6}
                  </Typography>
                  <br/>
                  <Typography variant="h6">
                    {text7}
                  </Typography>
                  <Typography variant="body1">
                    {text8}
                  </Typography>
                </div>
              )}
              <div className={classes.alignRight}>
                <Button color="primary" onClick={this.onTextExpand}>
                    {this.state.isExpanded ? 'Less ...' : 'More ...'}
                </Button>
              </div>
            </div>
          </Paper>
        </Grid>

      </Grid>
      </MuiPickersUtilsProvider>
    );
  }

};

export default withStyles(styles)(withErrorHandling(Commutation));

const text1 = `California Code, Labor Code - LAB § 5100`;
const text2 = `At the time of making its award, or at any time thereafter, the appeals board, on its own motion either upon notice, or upon application of either party with due notice to the other, may commute the compensation payable under this division to a lump sum and order it to be paid forthwith or at some future time if any of the following conditions appear:

(a) That such commutation is necessary for the protection of the person entitled thereto, or for the best interest of the applicant. In determining what is in the best interest of the applicant, the appeals board shall consider the general financial condition of the applicant, including but not limited to, the applicant's ability to live without periodic indemnity payments and to discharge debts incurred prior to the date of injury.

(b) That commutation will avoid inequity and will not cause undue expense or hardship to the applicant.

(c) That the employer has sold or otherwise disposed of the greater part of his assets or is about to do so.

(d) That the employer is not a resident of this state.
`;
const text3 = `California Code, Labor Code - LAB § 5100.5`;
const text4 = `Notwithstanding the provisions of Section 5100, the appeals board shall not commute the compensation payable under this division to a lump sum when such compensation is payable under Section 4751 of the Labor Code (Subsequent Injuries Payments).
`;
// 4751 - https://leginfo.legislature.ca.gov/faces/codes_displaySection.xhtml?lawCode=LAB&sectionNum=4751.
const text5 = `California Code, Labor Code - LAB § 5100.6`;
const text6 = `Notwithstanding the provisions of Section 5100, the appeals board shall not permit the commutation or settlement of prospective compensation or indemnity payments or other benefits to which the employee is entitled under vocational rehabilitation.
`;
const text7 = `California Code, Labor Code - LAB § 5101`;
const text8 = `The amount of the lump sum shall be determined as follows:

(a) If the injury causes temporary disability, the appeals board shall estimate the probable duration thereof and the probable amount of the temporary disability payments therefor, in accordance with Chapter 2 of Part 2 of this division, and shall fix the lump sum at the amount so determined.

(b) If the injury causes permanent disability or death, the appeals board shall fix the total amount of the permanent disability payment or death benefit payable therefor in accordance with Chapter 2 of Part 2 of this division, and shall estimate the present value thereof, assuming interest at the rate of 3 percent per annum and disregarding the probability of the beneficiary's death in all cases except where the percentage of permanent disability is such as to entitle the beneficiary to a life pension, and then taking into consideration the probability of the beneficiary's death only in estimating the present value of such life pension.
`;
